import React, { useState, useEffect, useRef } from 'react';

interface Seller {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  department?: string;
  submissionCount: number;
  fullName: string;
}

interface SellerAutocompleteProps {
  value: string;
  onChange: (value: string, seller?: Seller) => void;
  placeholder?: string;
  className?: string;
}

const SellerAutocomplete: React.FC<SellerAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Søk etter selger...",
  className = ""
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<Seller[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Fetch suggestions when input changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!inputValue.trim() || inputValue.length < 1) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/submissions/sellers?search=${encodeURIComponent(inputValue)}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const sellers: Seller[] = await response.json();
          setSuggestions(sellers);
          setShowSuggestions(true);
          setActiveSuggestion(-1);
        }
      } catch (error) {
        console.error('Error fetching seller suggestions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleSuggestionClick = (seller: Seller) => {
    setInputValue(seller.fullName);
    setShowSuggestions(false);
    onChange(seller.fullName, seller);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestion(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestion(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (activeSuggestion >= 0) {
          handleSuggestionClick(suggestions[activeSuggestion]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveSuggestion(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(e.relatedTarget as Node)) {
        setShowSuggestions(false);
        setActiveSuggestion(-1);
      }
    }, 150);
  };

  const clearInput = () => {
    setInputValue('');
    setShowSuggestions(false);
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div className="seller-autocomplete">
      <div className="autocomplete-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`autocomplete-input ${className}`}
          autoComplete="off"
        />
        {inputValue && (
          <button
            type="button"
            className="clear-button"
            onClick={clearInput}
            aria-label="Tøm søk"
          >
            ✕
          </button>
        )}
        {isLoading && (
          <div className="loading-indicator">
            <span>...</span>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="suggestions-dropdown"
        >
          {suggestions.map((seller, index) => (
            <div
              key={seller._id}
              className={`suggestion-item ${index === activeSuggestion ? 'active' : ''}`}
              onClick={() => handleSuggestionClick(seller)}
              onMouseEnter={() => setActiveSuggestion(index)}
            >
              <div className="seller-info">
                <div className="seller-name">{seller.fullName}</div>
                <div className="seller-details">
                  {seller.department && <span className="department">{seller.department}</span>}
                  <span className="submission-count">{seller.submissionCount} innsendinger</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SellerAutocomplete;